import { usePage } from "@inertiajs/react"
import FooterBackdrop from "@/components/FooterBackdrop"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  )
}

// The venue's footer: plain navy, or — when the admin has chosen a photo in
// the site-wide "Footer background" slot (edited from the Home page's Website
// screen) — that photo under the same navy film as a page hero, exactly as
// Gisela's footer does.
export default function PublicFooter() {
  const { translations, hidden_paths, current_brand } = usePage<PageProps>().props
  const phone = current_brand.phone
  const email = current_brand.email
  const sanctuary_locations = usePage<PageProps>().props.sanctuary_locations ?? []
  const lp = useLocalePath()
  const t = translations.footer
  const tn = translations.nav

  return (
    <footer className="relative overflow-hidden bg-navy text-white">
      <FooterBackdrop />
      <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/sanctuary-favicon.png"
                alt="Sanctuary Therapy Centre"
                className="h-10 w-10 rounded-full object-cover opacity-90"
              />
              <span className="text-sm font-medium tracking-wide text-white/90">
                {tn.site_name}
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              {t.tagline}
            </p>
            <div className="mt-5 flex gap-3">
              <a
                href="#"
                aria-label="Facebook"
                className="rounded-full bg-white/10 p-2 text-white/60 transition-colors hover:bg-white/20 hover:text-white"
              >
                <FacebookIcon className="h-4 w-4" />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="rounded-full bg-white/10 p-2 text-white/60 transition-colors hover:bg-white/20 hover:text-white"
              >
                <InstagramIcon className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Navigate */}
          <div>
            <h6 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
              {t.navigate}
            </h6>
            <ul className="space-y-2 text-sm text-white/70">
              {[
                { label: "Home",                 path: "/" },
                { label: tn.therapy_with_gisela, path: "/therapy-with-gisela" },
                { label: t.room_hire,            path: "/rent-a-room" },
                { label: tn.resources,           path: "/blog" },
                { label: "Gallery",              path: "/gallery" },
                { label: tn.contact,             path: "/contact" },
                { label: t.privacy_policy,       path: "/privacy-policy" },
              ]
                .filter(({ path }) => !hidden_paths.includes(path))
                .map(({ label, path }) => ({ label, href: lp(path) }))
                .map(({ label, href }) => (
                <li key={href}>
                  {/* The colour has to sit on the anchor, not be inherited
                      from the ul: design-system.css sets `a { color: ... }` in
                      its base layer, and an explicit rule beats inheritance —
                      which painted this whole list navy on the navy footer. */}
                  <a href={href} className="text-white/70 transition-colors hover:text-white">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h6 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
              {t.locations_header}
            </h6>
            {/* One entry per location, from the same shared prop as the nav's
                Locations menu — a location added in the admin appears here. */}
            <ul className="space-y-4 text-sm text-white/70">
              {sanctuary_locations.map((site) => (
                <li key={site.path}>
                  <a href={lp(site.path)} className="font-medium text-white/90 transition-colors hover:text-white">
                    {site.label}
                  </a>
                  <p className="mt-0.5 text-xs text-white/50">{site.address}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h6 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">
              {t.contact_details}
            </h6>
            <ul className="space-y-2 text-sm text-white/70">
              {phone && (
                <li>
                  <a
                    href={`tel:${phone.replace(/\s/g, "")}`}
                    className="text-white transition-colors hover:text-white/80"
                  >
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a
                    href={`mailto:${email}`}
                    className="text-white/70 transition-colors hover:text-white"
                  >
                    {email}
                  </a>
                </li>
              )}
              <li className="mt-4">
                <a
                  href={lp("/contact")}
                  className="inline-block rounded-full border border-white/30 px-4 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-white/60 hover:text-white"
                >
                  {t.send_enquiry}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="relative border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-4 lg:px-8">
          <p className="text-center text-xs text-white/30">
            {t.copyright.replace("%{year}", String(new Date().getFullYear()))}
          </p>
        </div>
      </div>
    </footer>
  )
}
